import React, { useEffect, useState } from "react";
import _ from "lodash";
import { connect } from "react-redux";
import classNames from "classnames";
import { compose, graphql } from "lib/apolloClient";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import {
  Avatar,
  Button,
  Checkbox,
  DSProvider,
  EmptyState,
  List,
  Modal,
  SearchInput,
} from "@toddle-edu/ds-web";
import { NoTemplatesIllustration } from "@toddle-edu/ds-theme";
import { ArrowOutOutlined } from "@toddle-edu/ds-icons";
import Loading from "UIComponents/Loading";

import classes from "./MealMapping.scss";
import {
  getOrganizationId,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import { getMealTemplatesQuery } from "../../modules/RoutineTemplatesQueries";

const styles = {
  modalHeaderStyles: { borderBottom: "1px solid var(--border-secondary)" },
  modalBodyStyles: { padding: "0px" },
  modalFooterStyles: {
    borderTop: "1px solid var(--border-secondary)",
    paddingTop: "16px",
  },
};
const ALL = "ALL";
export const MealMapping = ({
  field,
  mealTemplates,
  mealMenusTagged,
  isData,
  isLoading,
  onSearchMeal,
  searchText,
  closeMealMapping,
  updateMealMenus,
}) => {
  const { t } = useTranslation(["common", "journal"]);
  const [selectedMenus, setSelectedMenus] = useState([]);
  const dateClass = classNames({
    ["text-body-s"]: true,
    [classes.dateLabel]: true,
  });
  const titleClass = classNames({
    ["text-label"]: true,
    [classes.listTitle]: true,
    [classes.titleLabel]: true,
  });
  useEffect(() => {
    if (!_.isEmpty(mealMenusTagged)) {
      const selectedIds = _.map(mealMenusTagged, menu => _.get(menu, "id"));
      setSelectedMenus(selectedIds);
    }
  }, [mealMenusTagged]);

  const isAllSelected =
    _.size(selectedMenus) > 0 &&
    _.isEqual(
      _.sortBy(selectedMenus),
      _.sortBy(_.map(mealTemplates, template => _.get(template, "id")))
    );

  const onClickMenu = id => {
    let updatedOptions;
    if (id === ALL) {
      updatedOptions = isAllSelected
        ? []
        : _.map(mealTemplates, template => _.get(template, "id"));
    } else if (_.includes(selectedMenus, id)) {
      updatedOptions = _.filter(selectedMenus, menuId => menuId !== id);
    } else {
      updatedOptions = [...selectedMenus, id];
    }
    setSelectedMenus(updatedOptions);
  };

  const getMealOptions = ({ mealTemplates }) => {
    const options = [
      {
        id: ALL,
        value: (
          <div id={ALL} className={classes.listItem}>
            <Avatar
              id={ALL}
              testId="routine-templates-mealmapping-allavatar"
              dsVersion="2.0"
              shade={"light"}
              size={"medium"}
              shape="square"
              color={"neutral"}
              iconType="group"
            />
            <label
              className="text-label"
              data-test-id="routine-templates-mealmapping-alllabel"
            >
              {t("journal:all_menus")}
            </label>
          </div>
        ),
        suffix: (
          <Checkbox
            size="medium"
            isIndeterminate={!isAllSelected && !_.isEmpty(selectedMenus)}
            isChecked={isAllSelected}
            name="option"
          />
        ),
      },
    ];
    const mealOptions = _.map(mealTemplates, template => {
      const templateId = _.get(template, "id");
      const isChecked = _.includes(selectedMenus, templateId);
      return {
        id: _.get(template, "id"),
        value: (
          <div className={classes.listItem}>
            <Avatar
              id={_.get(template, "id")}
              testId={`routine-templates-mealmapping-menuavatar-${_.get(
                template,
                "id"
              )}`}
              name={_.get(template, "title")}
              dsVersion="2.0"
              shade={"light"}
              size={"medium"}
              shape="square"
            />
            <div
              className={classes.listTitle}
              data-test-id={`routine-templates-mealmapping-menutitle-${_.get(
                template,
                "id"
              )}`}
            >
              <label className={titleClass}>{_.get(template, "title")}</label>
              <label className={dateClass}>
                {t("journal:applicable_from_start_to_end", {
                  startDate: moment(
                    _.get(template, "startDate"),
                    DATE_TIME_CONSTANTS.YYYY_MM_DD
                  ).format(DATE_TIME_CONSTANTS.DD_MMM),
                  endDate: moment(
                    _.get(template, "endDate"),
                    DATE_TIME_CONSTANTS.YYYY_MM_DD
                  ).format(DATE_TIME_CONSTANTS.DD_MMM),
                })}
              </label>
            </div>
          </div>
        ),
        suffix: <Checkbox isChecked={isChecked} size="medium" name="option" />,
      };
    });
    return [...options, ...mealOptions];
  };

  const onConfirmMenus = () => {
    const updatedMenus = _.filter(mealTemplates, template => {
      const templateId = _.get(template, "id");
      return _.includes(selectedMenus, templateId);
    });
    updateMealMenus(updatedMenus, _.get(field, "id"));
    closeMealMapping();
  };

  const classFooter = classNames({
    [classes.footer]: _.size(selectedMenus) > 0,
    [classes.alignRight]: _.isEmpty(selectedMenus),
  });

  return (
    <div data-test-id="routine-templates-mealmapping-container">
      <DSProvider dsVersion="2.0">
        <Modal
          width={"480px"}
          height={"fit-content"}
          shouldCloseOnEsc={true}
          isOpen={true}
          shouldCloseOnOverlayClick={false}
          onClose={closeMealMapping}
          testId="routine-templates-mealmapping-modal"
        >
          <Modal.Header
            style={styles.modalHeaderStyles}
            isClosable={true}
            hasSeparator={false}
            testId="routine-templates-mealmapping-header"
          >
            {t("journal:map_meal_to_this_template")}
          </Modal.Header>
          <Modal.Body style={styles.modalBodyStyles}>
            {!isData && !isLoading ? (
              <EmptyState
                illustration={NoTemplatesIllustration}
                size="small"
                title={t("journal:no_meal_plans_have_been_created")}
                subtitle={t(
                  "journal:click_on_button_to_start_creating_meal_plans"
                )}
                testId="routine-templates-mealmapping-empty"
                primaryButton={
                  <Button
                    size="medium"
                    type="fill"
                    variant="destructive"
                    rightIcon={<ArrowOutOutlined />}
                    testId="routine-templates-mealmapping-create"
                  >
                    {t("journal:create_meal_plan")}
                  </Button>
                }
              />
            ) : (
              <>
                <div className={classes.searchContainer}>
                  <SearchInput
                    dsVersion="2.0"
                    focusSearchBySlash={true}
                    value={searchText}
                    onChange={onSearchMeal}
                    size="medium"
                    placeholder={t("common:search_placeholder")}
                    testId="routine-templates-mealmapping-search"
                  />
                </div>
                <div
                  className={classes.listContainer}
                  data-test-id="routine-templates-mealmapping-listcontainer"
                >
                  <List
                    onClick={onClickMenu}
                    showSelection={false}
                    listItemClassName={classes.listItem}
                    data={getMealOptions({
                      mealTemplates,
                    })}
                    rowsSelected={selectedMenus}
                    selectionType="checkbox"
                    selectItemPosition="right"
                    isDraggable={false}
                    testId="routine-templates-mealmapping-list"
                  />
                  {isLoading && <Loading testId="routine-mealmapping-loader" />}
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer
            style={styles.modalFooterStyles}
            hasSeparator={false}
            testId="routine-templates-mealmapping-footer"
          >
            <div className={classFooter}>
              {_.size(selectedMenus) > 0 ? (
                <label
                  className="text-label"
                  data-test-id="routine-templates-mealmapping-selectioncount"
                >
                  {t(
                    _.size(selectedMenus) === 1
                      ? "journal:count_menus_selected"
                      : "journal:count_menus_selected_plural",
                    {
                      count: _.size(selectedMenus),
                    }
                  )}
                </label>
              ) : null}
              <div className={classes.footerButtons}>
                <Button
                  size="medium"
                  type="outlined"
                  variant="neutral"
                  onClick={closeMealMapping}
                  testId="routine-templates-mealmapping-cancel"
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  size="medium"
                  variant="destructive"
                  onClick={onConfirmMenus}
                  testId="routine-templates-mealmapping-confirm"
                >
                  {t("common:confirm")}
                </Button>
              </div>
            </div>
          </Modal.Footer>
        </Modal>
      </DSProvider>
    </div>
  );
};

export const mapStateToProps = (state, { searchText }) => {
  const organizationId = getOrganizationId(state);
  const curriculumProgramId = getSelectedCurriculumId(state);
  const mealVariables = {
    organizationId,
    filters: {
      searchText,
      curriculumProgramIds: [curriculumProgramId],
      dataSetType: "MEAL_MENU",
    },
  };
  return { organizationId, mealVariables };
};

export default compose(
  connect(mapStateToProps, null),
  graphql(getMealTemplatesQuery, {
    name: "getMealMenus",
    options: ({ mealVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: mealVariables,
    }),
    props: ({ getMealMenus }) => {
      const data = _.get(getMealMenus, "node", {});
      const mealTemplates = _.get(data, "postTemplateDataSet", []);
      return {
        mealTemplates,
        isData: !_.isEmpty(data),
        isLoading: _.includes([1, 2, 3], getMealMenus["networkStatus"]),
      };
    },
  })
)(MealMapping);
